<template>
    <div class="container -documents">
        <ol class="breadcrumb">
            <li><a v-link="{name: 'home'}">Accueil</a></li>
            <li>Documents</li>
        </ol>

        <div class="heading f wrap -a-center">
            <h1 class="expand">Types de documents</h1>

            <a v-link="{name: 'document-types-add'}"
               class="btn btn-primary -add">
                <i class="glyphicon glyphicon-plus"></i> Ajouter un document
            </a>
        </div>

        <div v-if="$loadingRouteData">
            <loader></loader>
        </div>

        <div v-if="!$loadingRouteData">
            <div class="jumbotron empty-list"
                 v-if="types && types.length === 0 && !searchQuery">
                <h2>Aucun type de documents</h2>

                <p>Il semble qu'aucun type de document ne soit défini. Les types de document
                    permettent d'établir les différents documents nécessaires dans la relation
                    avec un membre, que ce soit un formulaire de souscription ou une pièce
                    d'identité.</p>

                <p class="cta">
                    <a v-link="{name: 'document-types-add'}"
                       class="btn btn-lg btn-primary">
                        <i class="glyphicon glyphicon-plus"></i> Ajouter un document
                    </a>
                </p>
            </div>

            <div class="search-row f -a-center">
                <div class="form-group expand">
                    <label for="clients-search"
                           class="sr-only">
                        Chercher un membre
                    </label>
                    <div class="input-group">
                        <div class="input-group-addon">
                            <loader v-show="loading"></loader>
                            <i v-show="!loading" class="glyphicon glyphicon-search"></i>
                        </div>
                        <input type="text"
                               class="form-control"
                               id="clients-search"
                               v-model="searchQuery"
                               placeholder="Chercher un nom, une compagnie, etc">
                    </div>
                </div>
            </div>

            <div class="filters">
                <radio-group :value.sync="archived" type="primary">
                    <radio-btn value="no" checked>Valides</radio-btn>
                    <radio-btn value="yes">Archivés</radio-btn>
                    <radio-btn value="both">Les deux</radio-btn>
                </radio-group>
            </div>

            <div v-if="types && types.length">
                <table class="table table-hover -documents -row-links">
                    <tr>
                        <th>Nom</th>
                        <th>Modèle</th>
                        <th>Compagnie</th>
                        <th>Type de contrat</th>
                    </tr>

                    <tr v-for="type in types">
                        <td><a v-link="{name: 'document-types-edit', params: {id: type.id}}">
                            {{ type.name }}
                        </a></td>
                        <td><a v-link="{name: 'document-types-edit', params: {id: type.id}}">
                            <span class="preview-cell f -a-center"
                                  v-if="type.template">
                                <i class="fa fa-{{ type.template.mime | mimeIcon }}"></i>
                                <div class="filename">
                                    {{ type.template.content | filename }}
                                </div>

                                <div class="popover bottom">
                                    <div class="arrow"></div>
                                    <div class="popover-content">
                                        <img v-bind:src="type.template.preview"
                                             alt="">
                                    </div>
                                </div>
                            </span>
                        </a></td>
                        <td><a v-link="{name: 'document-types-edit', params: {id: type.id}}">
                            <em v-if="!type.company">aucune</em>
                            <span v-if="type.company">
                                {{ companiesDb[type.company].name }}
                            </span>
                        </a></td>
                        <td><a v-link="{name: 'document-types-edit', params: {id: type.id}}">
                            <em v-if="!type.contract_type">aucun</em>
                            <span v-if="type.contract_type">
                                {{ companiesDb[type.company].types[type.contract_type].name }}
                            </span>
                        </a></td>
                    </tr>
                </table>

                <loader v-show="loading" class="bigger-loader"></loader>

                <div class="load-more">
                    <a href="#"
                       v-if="hasNext && !loading"
                       v-on:click.prevent="updateList(true)"
                       class="btn btn-default">
                        Charger plus
                    </a>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped
       lang="less">
    @import '~src/style/variables';

    @popover-width: 300px;

    .empty-list {
        margin-top: @grid-gutter-width;
    }

    .cta {
        text-align: center;
        margin-top: @grid-gutter-width;
    }

    .preview-cell {
        position: relative;

        .popover {
            display: block;
            visibility: hidden;
            opacity: 0;

            top: 100%;
            left: 6px;
            transform: translateX(-50%);
            max-width: @popover-width;

            transition: ease all 200ms;
            transition-delay: 0ms;
        }

        &:hover .popover {
            visibility: visible;
            opacity: 1;
            transition-delay: 300ms;
        }

        img {
            max-width: @popover-width - 30px;
        }

        .filename {
            margin-left: 5px;
            overflow: hidden;
            white-space: nowrap;
        }
    }

    td:nth-child(3) {
        width: 170px;
    }

    td:nth-child(4) {
        width: 170px;
    }

    .load-more {
        text-align: center;
    }

    .filters {
        margin-bottom: @grid-gutter-width;
    }
</style>

<script type="text/ecmascript-6">
    import {list as listDocTypes} from '../api/document_types';
    import {makeUpdateList, tryOr404} from '../utils';
    import {getAllCompaniesMap} from '../api/companies';
    import Loader from '../components/Loader';
    import RadioGroup from 'vue-strap/src/radioGroup';
    import RadioBtn from 'vue-strap/src/radioBtn';

    export default {
        components: {
            RadioGroup,
            RadioBtn,
            Loader
        },
        data() {
            return {
                companiesDb: undefined,
                searchQuery: '',
                types: undefined,
                loading: false,
                archived: 'no',
                page: 1,
                hasNext: false
            };
        },
        route: {
            data: tryOr404(async function () {
                return {
                    companiesDb: await getAllCompaniesMap()
                };
            })
        },
        watch: {
            searchQuery() {
                this.updateList();
            },
            archived() {
                this.updateList();
            }
        },
        methods: {
            updateList: makeUpdateList({
                loading: 'loading',
                page: 'page',
                hasNext: 'hasNext',
                list: 'types',
                fetch: async function (page) {
                    return await listDocTypes(page, this.searchQuery, this.archived);
                }
            })
        },
        ready() {
            this.updateList();
        }
    };
</script>



// WEBPACK FOOTER //
// Documents.vue?fb8a13da